import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/customer-ui/button'
import { Input } from '@/components/customer-ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import zeresLogo from '@/assets/logo.webp'
import { Eye, EyeOff } from 'lucide-react'
import client from '@/http/customer-client'
import { isAxiosError } from 'axios'
import evHero from '../../ev-journey/assets/ev-hero.jpg'
import zeresLogoWhite from '@/assets/logo-dark.webp'

export const registerLoginStorageKey = 'zeres.registerLogin'

interface RegisterPayload {
  email: string
  first_name: string
  last_name: string
  password: string
  partner_discount_code?: string | null
}

const apiCustomemrRegister = async (data: RegisterPayload) => {
  return await client.post('/app-api/register', {
    ...data,
    intent: 'customer'
  })
}

const getPartnerDiscountCode = (search: string, hash: string) => {
  const match = search.match(/[?&]partner=/)
  if (!match || match.index === undefined) {
    return null
  }

  const code = `${search.slice(match.index + match[0].length)}${hash}`

  return code === '' ? null : code
}

const PageRegister = () => {
  const navigate = useNavigate()
  const { hash, search } = useLocation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const partnerDiscountCode = getPartnerDiscountCode(search, hash)

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!firstName || !lastName || !email || !password) {
      toast.error('Vul alle velden in')
      return
    }

    const passwordErrors = []

    if (password.length < 4) {
      passwordErrors.push('Wachtwoord moet minstens 4 tekens bevatten')
    }

    if (!/[a-zA-Z]/.test(password)) {
      passwordErrors.push('Wachtwoord moet minstens 1 letter bevatten')
    }

    if (!/\d/.test(password)) {
      passwordErrors.push('Wachtwoord moet minstens 1 cijfer bevatten')
    }

    if (passwordErrors.length > 0) {
      passwordErrors.forEach((err) => toast.error(err))
      return
    }

    if (!agreed) {
      toast.error('U dient akkoord te gaan met het mandaat')
      return
    }

    setIsLoading(true)

    try {
      await apiCustomemrRegister({
        email,
        first_name: firstName,
        last_name: lastName,
        password,
        partner_discount_code: partnerDiscountCode
      })

      window.sessionStorage.setItem(
        registerLoginStorageKey,
        JSON.stringify({ email, password })
      )
      void navigate('/customer/register-successful', {
        state: { email, password },
        replace: true
      })
    } catch (error) {
      if (isAxiosError(error)) {
        const err = error.response?.data as {
          message: string | null
          code: number
        }

        toast.error(err.message || 'Er is iets fout gegaan')
      } else {
        toast.error('Er is iets fout gegaan')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='flex min-h-screen flex-col lg:flex-row'>
      {/* Left - Hero Image with Info Text */}
      <div className='relative hidden overflow-hidden lg:flex lg:w-1/2'>
        <img
          src={evHero}
          alt='Elektrisch rijden met zonne-energie'
          className='absolute inset-0 h-full w-full object-cover'
        />
        <div className='from-foreground/10 via-foreground/30 to-foreground/70 absolute inset-0 bg-linear-to-b' />
        <div className='relative z-10 flex h-full flex-col justify-between p-12'>
          {/* Info box at top */}
          <div className='bg-primary/80 text-primary-foreground space-y-3 rounded-xl p-6 text-sm leading-relaxed backdrop-blur-sm'>
            <h1 className='text-2xl font-bold'>Welkom bij Zeres</h1>
            <p>
              U kunt zich hier aanmelden voor de ERE-vergoeding. In deze veilige
              omgeving kunt u de informatie over uw stroomaansluiting, uw adres,
              het bankrekeningnummer en de laadpaal vermelden. Aan het einde van
              ieder kwartaal moet u het totaal aan laadstroom aan ons doorgeven,
              dat kan via deze portal.
            </p>
            <div className='grid grid-cols-[70px_auto] gap-y-1 pl-2'>
              <span>Email:</span>
              <a
                href='mailto:helpdesk@zeres.nl'
                className='underline hover:opacity-80'
              >
                helpdesk@zeres.nl
              </a>

              <span>Telefoon:</span>
              <a href='tel:0850600641' className='underline hover:opacity-80'>
                085 – 060 0641
              </a>
            </div>
            <p className='font-medium'>
              Aanmelden: u kiest zelf een wachtwoord.
            </p>
          </div>
          {/* Logo at bottom */}
          <div className='mt-auto'>
            <img
              src={zeresLogoWhite}
              alt='Zeres - Zero Emission Services'
              className='h-10 w-auto'
            />
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className='bg-background flex flex-1 items-center justify-center p-6 sm:p-12'>
        <div className='animate-fade-in w-full max-w-md'>
          <div className='mb-10'>
            <div className='mb-8 flex justify-center'>
              <img
                src={zeresLogo}
                alt='Zeres - Zero Emission Services'
                className='h-16 w-auto'
              />
            </div>
            <h2 className='text-foreground mb-2 text-2xl font-bold'>
              Aanmelden
            </h2>
            <p className='text-muted-foreground'>
              Maak een nieuw Zeres account aan:
            </p>
          </div>

          {/* Mobile hero banner */}
          <div className='relative mb-8 h-40 overflow-hidden rounded-lg lg:hidden'>
            <img
              src={evHero}
              alt='Elektrisch rijden'
              className='h-full w-full object-cover'
            />
            <div className='from-foreground/60 absolute inset-0 bg-linear-to-t to-transparent' />
            <div className='absolute right-4 bottom-3 left-4'>
              <p className='text-primary-foreground text-sm font-medium'>
                Welkom bij Zeres – Meld u aan voor de ERE-vergoeding ⚡
              </p>
            </div>
          </div>

          <form onSubmit={(e) => void handleRegister(e)} className='space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='firstName'>Voornaam</Label>
                <Input
                  id='firstName'
                  type='text'
                  placeholder='Uw voornaam'
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value)
                  }}
                  className='h-11'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='lastName'>Achternaam</Label>
                <Input
                  id='lastName'
                  type='text'
                  placeholder='Uw achternaam'
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value)
                  }}
                  className='h-11'
                />
              </div>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='regEmail'>E-mailadres</Label>
              <Input
                id='regEmail'
                type='email'
                placeholder='naam@voorbeeld.nl'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                }}
                className='h-11'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='regPassword'>Wachtwoord</Label>
              <div className='relative'>
                <Input
                  id='regPassword'
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Kies een wachtwoord'
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                  }}
                  className='h-11 pr-12'
                />
                <button
                  type='button'
                  onClick={() => {
                    setShowPassword(!showPassword)
                  }}
                  className='text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors'
                >
                  {showPassword ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            <div className='space-y-3 pt-2'>
              <Label className='text-sm font-semibold'>Voor akkoord</Label>
              <div className='flex items-start gap-3'>
                <Checkbox
                  id='mandate'
                  checked={agreed}
                  onCheckedChange={(checked) => {
                    setAgreed(checked === true)
                  }}
                  className='mt-0.5 size-5 rounded-full border-[3px] border-black shadow-none data-[state=checked]:border-[#16a34a] data-[state=checked]:bg-[#16a34a] data-[state=checked]:text-white'
                />
                <Label
                  htmlFor='mandate'
                  className='text-muted-foreground cursor-pointer text-sm leading-relaxed font-normal'
                >
                  Ik geef Zeres het mandaat om namens mij ERE's bij de
                  Nederlandse Emissieautoriteit in te boeken, te verhandelen en
                  na aftrek van de commissie aan mij uit te betalen.
                </Label>
              </div>
            </div>

            <Button
              type='submit'
              className='h-11 w-full text-base font-semibold'
              disabled={isLoading || !agreed}
            >
              {isLoading ? 'Aanmelden...' : 'Aanmelden'}
            </Button>
          </form>

          <p className='text-muted-foreground mt-6 text-center text-sm'>
            Heeft u al een account?
            <button
              onClick={() => void navigate('/customer/login')}
              className='text-primary ml-4 cursor-pointer font-medium hover:underline'
            >
              Inloggen
            </button>
          </p>

          <div className='text-muted-foreground mt-6 space-y-1 border-t pt-6 text-center text-sm'>
            <p>
              Helpdesk:{' '}
              <a
                href='tel:0850600641'
                className='hover:text-primary transition-colors'
              >
                085 – 060 0641
              </a>
            </p>
            <p>
              <a
                href='mailto:helpdesk@zeres.nl'
                className='hover:text-primary transition-colors'
              >
                helpdesk@zeres.nl
              </a>
            </p>
            <p>
              <a
                href='https://www.zeres.nl'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-primary transition-colors'
              >
                www.zeres.nl
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PageRegister
